import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { DefaultFilters } from '@backstage/plugin-catalog-react';
import { EntityValidationPage } from '@backstage-community/plugin-entity-validation';

import {
  AlertDisplay,
  ErrorPage,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import * as Sentry from '@sentry/react';
import { createApp } from '@backstage/app-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { hkidOAuthProvider } from './auth-providers/hkid';
import { herokuTheme } from './theme/heroku';
import { UnifiedThemeProvider } from '@backstage/theme';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { MoreFiltersButton } from './components/catalog/filters/MoreFiltersButton';
import { SchemaPage } from './components/schema/SchemaPage';
import { MigrationPage } from './components/migration/MigrationPage';
import { CatalogCoveragePage } from '@internal/backstage-plugin-catalog-coverage';

const exampleCatalogYaml = `# Paste your catalog-info.yaml here
apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: example-component
spec:
  type: service
  lifecycle: non-production
  owner: group:default/example-team
`;

const app = createApp({
  apis,
  themes: [
    {
      id: 'heroku',
      title: 'Heroku',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={herokuTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          hkidOAuthProvider,
          ...(process.env.NODE_ENV === 'development' &&
          process.env.BACKSTAGE_DEV_GUEST_LOGIN === 'true'
            ? ['guest' as const]
            : []),
        ]}
      />
    ),
  },
});

const AppProvider = app.getProvider();
const AppRouter = app.getRouter();

export default function App() {
  return (
    <AppProvider>
      <Sentry.ErrorBoundary
        fallback={({ error }) => (
          <ErrorPage
            status="Error"
            statusMessage={
              error instanceof Error ? error.message : 'Unknown error'
            }
          />
        )}
      >
        <AlertDisplay />
        <OAuthRequestDialog />
        <AppRouter>
          <Root>
            <FlatRoutes>
              <Route path="/" element={<HomepageCompositionRoot />}>
                <HomePage />
              </Route>
              <Route
                path="/catalog"
                element={
                  <CatalogIndexPage
                    filters={
                      <>
                        <DefaultFilters />
                        <MoreFiltersButton />
                      </>
                    }
                  />
                }
              />
              <Route
                path="/catalog/:namespace/:kind/:name"
                element={<CatalogEntityPage />}
              >
                {entityPage}
              </Route>
              <Route path="/docs" element={<TechDocsIndexPage />} />
              <Route
                path="/docs/:namespace/:kind/:name/*"
                element={<TechDocsReaderPage />}
              >
                <TechDocsAddons>
                  <ReportIssue />
                </TechDocsAddons>
              </Route>
              <Route path="/create" element={<ScaffolderPage />} />
              <Route path="/api-docs" element={<ApiExplorerPage />} />
              <Route
                path="/catalog-import"
                element={
                  <RequirePermission permission={catalogEntityCreatePermission}>
                    <CatalogImportPage />
                  </RequirePermission>
                }
              />
              <Route path="/search" element={<SearchPage />}>
                {searchPage}
              </Route>
              <Route path="/catalog-schema" element={<SchemaPage />} />
              <Route path="/catalog-migration" element={<MigrationPage />} />
              <Route
                path="/catalog-coverage"
                element={<CatalogCoveragePage />}
              />
              <Route
                path="/entity-validation"
                element={
                  <EntityValidationPage
                    defaultYaml={exampleCatalogYaml}
                    defaultLocation="https://github.com/heroku/example"
                  />
                }
              />
              <Route path="/settings" element={<UserSettingsPage />} />
              <Route path="/catalog-graph" element={<CatalogGraphPage />} />
            </FlatRoutes>
          </Root>
        </AppRouter>
      </Sentry.ErrorBoundary>
    </AppProvider>
  );
}
